import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    Alert,
    Autocomplete,
    Button,
    Card,
    CardActionArea,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Radio,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import type {ResourceCategory} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import type {Service} from "crqlar-react-common/dist/models/service/service.model";
import {FloorplanService} from "crqlar-react-common/dist/services/floorplan/floorplan.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {
    useCurrentApp,
    useCurrentAppId,
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useAllResourceCategoriesForSector} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useServicesForSector} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useSnackbar} from "notistack";
import {type ChangeEvent, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {useNavigate} from "react-router-dom";

interface CreateRestaurantDialogProps {
    open: boolean;
    onClose: () => void;
    onCreated: (restaurantId: string) => void;
}

export function CreateRestaurantDialog({
    open,
    onClose,
    onCreated,
}: CreateRestaurantDialogProps) {
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();
    const appId = useCurrentAppId();
    const appData = useCurrentApp();
    const sector = useSector(appData?.configuration?.sectorId);
    const resourceCategories = useAllResourceCategoriesForSector(sector?.id);
    const services = useServicesForSector(sector?.id);

    const [createMode, setCreateMode] = useState<"manual" | "upload">("manual");
    const [restaurantName, setRestaurantName] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<ResourceCategory | null>(null);
    const [selectedServices, setSelectedServices] = useState<Service[]>([]);
    const [floorplanFile, setFloorplanFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    useEffect(() => {
        if (!saving || createMode !== "upload") {
            setElapsedSeconds(0);
            return;
        }

        const interval = window.setInterval(
            () => setElapsedSeconds(current => current + 1),
            1000,
        );
        return () => window.clearInterval(interval);
    }, [createMode, saving]);

    useEffect(() => {
        if (open && resourceCategories.length === 1 && !selectedCategory) {
            setSelectedCategory(resourceCategories[0]);
        }
    }, [open, resourceCategories, selectedCategory]);

    const resetForm = () => {
        setCreateMode("manual");
        setRestaurantName("");
        setSelectedCategory(null);
        setSelectedServices([]);
        setFloorplanFile(null);
        setSaving(false);
        setElapsedSeconds(0);
    };

    const handleClose = () => {
        onClose();
        resetForm();
    };

    const formatElapsed = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
    };

    const getCreatedRestaurantId = (
        result: Awaited<ReturnType<typeof FloorplanService.createRestaurant>>,
    ) => result.restaurant?.id || result.restaurantId || result.resourceId || result.id;

    const getCreateErrorMessage = (error: unknown) => {
        if (createMode === "upload" && axios.isAxiosError(error) && error.response?.status === 500) {
            return t("We could not recognize this floorplan. Please upload a clearer image or PDF.");
        }
        return t("Failed to create restaurant");
    };

    const assignServicesToRestaurant = async (restaurantId: string) => {
        if (selectedServices.length === 0) return;

        await Promise.all(
            selectedServices.map(service => ServiceService.updateService({
                ...service,
                resourceIds: Array.from(new Set([...(service.resourceIds ?? []), restaurantId]))
                    .filter((resourceId): resourceId is string => Boolean(resourceId)),
            })),
        );
    };

    const finishRestaurantCreation = async (restaurantId: string) => {
        await assignServicesToRestaurant(restaurantId);
        await queryClient.invalidateQueries(["allRootResourcesForSector", sector?.id]);
        await queryClient.invalidateQueries("allServices", {exact: false});
        onCreated(restaurantId);
        handleClose();
        enqueueSnackbar(t("Restaurant created"), {variant: "success"});
        const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        navigate(
            `/restaurant/floorplan/${appId}/${sector!.id!}/${restaurantId}/edit?returnTo=${encodeURIComponent(returnTo)}`,
        );
    };

    const handleFloorplanFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0] ?? null;
        if (!selectedFile) {
            setFloorplanFile(null);
            return;
        }

        const isPdf = selectedFile.type === "application/pdf"
            || selectedFile.name.toLowerCase().endsWith(".pdf");
        const isImage = selectedFile.type.startsWith("image/");
        if (!isPdf && !isImage) {
            setFloorplanFile(null);
            enqueueSnackbar(t("Please upload an image or PDF file"), {variant: "error"});
            return;
        }
        setFloorplanFile(selectedFile);
    };

    const handleCreateRestaurant = async () => {
        const trimmedName = restaurantName.trim();
        if (!trimmedName || !sector?.id || saving || (createMode === "upload" && !floorplanFile)) return;

        try {
            setSaving(true);
            if (createMode === "manual") {
                const created = await ResourceService.createResource({
                    name: trimmedName,
                    sectorIds: [sector.id],
                    categoryIds: selectedCategory?.id ? [selectedCategory.id] : [],
                    calculateCapacityFromChildren: true,
                    meta: {seatsAroundTable: false},
                });
                if (!created.id) throw new Error("Created restaurant has no id");
                await finishRestaurantCreation(created.id);
                return;
            }

            const result = await FloorplanService.createRestaurant(
                trimmedName,
                sector.id,
                floorplanFile as File,
                selectedCategory?.id,
            );
            const restaurantId = getCreatedRestaurantId(result);
            if (!restaurantId) throw new Error("Created restaurant has no id");
            await finishRestaurantCreation(restaurantId);
        } catch (error) {
            enqueueSnackbar(getCreateErrorMessage(error), {variant: "error"});
        } finally {
            setSaving(false);
        }
    };

    const renderCreateModeCard = (
        mode: "manual" | "upload",
        title: string,
        description: string,
    ) => {
        const selected = createMode === mode;

        return (
            <Card
                variant="outlined"
                sx={{
                    flex: 1,
                    borderRadius: 1,
                    borderColor: selected ? "primary.main" : "divider",
                }}
            >
                <CardActionArea onClick={() => setCreateMode(mode)} sx={{height: "100%"}}>
                    <Stack direction="row" alignItems="flex-start" gap={1} sx={{p: 1.5}}>
                        <Radio checked={selected} size="small"/>
                        <Stack gap={0.5}>
                            <Typography variant="subtitle2">{title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {description}
                            </Typography>
                        </Stack>
                    </Stack>
                </CardActionArea>
            </Card>
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>{t("Create New Restaurant")}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                        {renderCreateModeCard(
                            "manual",
                            t("Manual creation"),
                            t("Create an empty restaurant resource."),
                        )}
                        {renderCreateModeCard(
                            "upload",
                            t("Upload floorplan"),
                            t("Create from an image or PDF."),
                        )}
                    </Stack>

                    <TextField
                        label={t("Restaurant name")}
                        value={restaurantName}
                        onChange={event => setRestaurantName(event.target.value)}
                        size="small"
                        fullWidth
                        required
                        autoFocus
                    />

                    {createMode === "upload" && (
                        <>
                            <Alert severity="info">
                                {t("AI processing may take some time after uploading an image or PDF.")}
                            </Alert>
                            <Stack gap={0.75}>
                                <Typography variant="body2" color="text.secondary">
                                    {t("Floorplan file")}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<UploadFileIcon/>}
                                    disabled={saving}
                                >
                                    {floorplanFile?.name || t("Upload image or PDF")}
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*,application/pdf,.pdf"
                                        onChange={handleFloorplanFileChange}
                                    />
                                </Button>
                            </Stack>
                        </>
                    )}

                    <Stack gap={0.75}>
                        <Typography variant="body2" color="text.secondary">
                            {t("Resource Category")}
                        </Typography>
                        <Autocomplete<ResourceCategory>
                            options={resourceCategories}
                            value={selectedCategory}
                            onChange={(_event, value) => setSelectedCategory(value)}
                            getOptionLabel={option => option.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={params => (
                                <TextField {...params} size="small" placeholder={t("Select category")}/>
                            )}
                            fullWidth
                        />
                    </Stack>

                    <Stack gap={0.75}>
                        <Typography variant="body2" color="text.secondary">
                            {t("Services")}
                        </Typography>
                        <Autocomplete<Service, true>
                            multiple
                            options={services}
                            value={selectedServices}
                            onChange={(_event, value) => setSelectedServices(value)}
                            getOptionLabel={option => option.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={params => (
                                <TextField {...params} size="small" placeholder={t("Select services")}/>
                            )}
                            fullWidth
                        />
                    </Stack>

                    <DialogActions sx={{px: 0, pb: 0}}>
                        <Button onClick={handleClose} disabled={saving}>
                            {t("Cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleCreateRestaurant}
                            disabled={
                                readOnly
                                || !restaurantName.trim()
                                || !sector?.id
                                || saving
                                || (createMode === "upload" && !floorplanFile)
                            }
                            sx={{
                                minWidth: createMode === "upload" ? 148 : 96,
                                whiteSpace: "nowrap",
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {saving && createMode === "upload"
                                ? `${t("Creating...")} ${formatElapsed(elapsedSeconds)}`
                                : saving ? t("Creating...") : t("Create")}
                        </Button>
                    </DialogActions>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}

export default CreateRestaurantDialog;
